import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { SignupForm } from "@/components/SignupForm";
import { buildLocalizedSeoLinks, getLocalizedSeoUrls } from "@/lib/seo";
import { useSession } from "../hooks/auth-hooks";

export const Route = createFileRoute("/signup")({
	component: SignupPage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/signup" });
		const title = "Create your AgentMRR account";
		const description =
			"Sign up for AgentMRR to list your AI agent, verify revenue with Stripe, and earn a spot on the public MRR leaderboard.";
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ name: "robots", content: "noindex, follow" },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/signup" }),
		};
	},
});

function SignupPage() {
	const { session, user, isPending } = useSession();

	const redirectParam =
		typeof window !== "undefined"
			? new URLSearchParams(window.location.search).get("redirect")
			: null;
	const postAuthUrl = redirectParam || "/dashboard";

	const shouldRedirectToDashboard = !isPending && !!session && !!user;

	useEffect(() => {
		if (!shouldRedirectToDashboard) return;
		window.location.assign(postAuthUrl);
	}, [shouldRedirectToDashboard, postAuthUrl]);

	if (shouldRedirectToDashboard) return null;
	if (isPending) return <LoadingScreen />;

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground pt-[4.5rem]">
			<PublicHeader />
			<main className="flex flex-1 flex-col items-center justify-center px-4 py-12">
				<div className="w-full max-w-[420px]">
					<div className="mb-8">
						<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							&gt; Auth / signup
						</span>
					</div>

					<div className="border border-border bg-card p-6 sm:p-8">
						<div className="mb-6 space-y-2">
							<h1
								className="font-mono text-2xl leading-tight tracking-[-0.02em]"
								style={{ fontWeight: 400 }}
							>
								<span className="text-brand">/</span>signup
							</h1>
							<p className="text-base text-muted-foreground">
								Create a free account to list your agent and verify its MRR.
							</p>
						</div>

						<SignupForm postAuthUrl={postAuthUrl} autoFocus />
					</div>

					<p className="mt-6 text-center font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
						Have an account?{" "}
						<Link
							to="/login"
							search={redirectParam ? { redirect: redirectParam } : {}}
							className="text-brand transition-colors hover:text-brand-hover"
						>
							Log in
						</Link>
					</p>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
